import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { T, MONO } from './poyais-ui/theme';
import { Icon } from './poyais-ui/icons';
import { ScreenHeader } from './poyais-ui/shell';
import { Card, Grid, Badge, Button } from './poyais-ui/primitives';
import PanoDetail from './PanoDetail';
import SalesBoard from './SalesBoard';
import { useLang, addTranslations } from './i18n';
import PoyaisSpinner from './PoyaisSpinner';

addTranslations({
  'Panolar': 'Boards',
  "Bağlı platformlardan canlı widget'larla kendi panonu oluştur.": 'Build your own board with live widgets from connected platforms.',
  'Yeni Pano': 'New Board',
  'Yükleniyor…': 'Loading…',
  'Yeniden adlandır': 'Rename',
  'Panoyu sil': 'Delete board',
  'Yeni pano oluştur': 'Create new board',
  'Satış Panosu': 'Sales Board',
  'Tüm satış kanallarının cirosu, siparişi, ürünleri ve finansı.': 'Revenue, orders, products and finance across all sales channels.',
  'Satış Panosu · Canlı': 'Sales Board · Live',
  'Canlı': 'Live',
  'Henüz widget eklenmedi': 'No widgets added yet',
  'Widget': 'Widget',
  'Son güncelleme': 'Last updated',
  'Bugün': 'Today',
  'Dün': 'Yesterday',
  '—': '—',
});

function relTime(iso?: string | null, lang: 'tr'|'en' = 'tr'): string {
  if (!iso) return '—';
  const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
  if (isNaN(d.getTime())) return '—';
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return lang === 'en' ? 'Today' : 'Bugün';
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return lang === 'en' ? 'Yesterday' : 'Dün';
  return d.toLocaleDateString(lang === 'en' ? 'en-US' : 'tr-TR', { day: '2-digit', month: 'short' });
}

const API = '/api/v1';

type Pano = {
  id: number; icon: string; icon_bg: string; icon_color: string;
  tag: string; tag_bg: string; tag_color: string;
  title: string; subtitle: string; kind?: 'custom' | 'sales';
  stats: { label: string; value: string; color?: string }[];
  widget_count?: number; last_widget_update?: string | null;
};

export default function Panolar({ clientId = 1 }: { clientId?: number }) {
  const { t, lang } = useLang();
  const [panolar, setPanolar] = useState<Pano[]>([]);
  const [loading, setLoading] = useState(true);
  const [openBoard, setOpenBoard] = useState<Pano | null>(null);
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const load = () => {
    axios.get(`${API}/panolar/?client_id=${clientId}`).then(r => setPanolar(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [clientId]);

  const addPano = () => {
    const n = panolar.length + 1;
    axios.post(`${API}/panolar/?client_id=${clientId}`, {
      icon: 'panolarGrid', icon_bg: 'var(--py-track-bg)', icon_color: 'var(--py-text-mid-alt)',
      tag: t('Canlı'), tag_bg: 'var(--py-green-tint)', tag_color: 'var(--py-green-text)',
      title: lang==='en' ? `New Board ${n}` : `Yeni Pano ${n}`,
    }).then(r => setPanolar(ps => [{ ...r.data, widget_count: 0, last_widget_update: null }, ...ps])).catch(() => {});
  };

  const removePano = (id: number) => {
    axios.delete(`${API}/panolar/${id}?client_id=${clientId}`).then(() => setPanolar(ps => ps.filter(p => p.id !== id))).catch(() => {});
  };

  const startRename = (p: Pano) => { setRenamingId(p.id); setRenameValue(p.title); };
  const saveRename = (id: number) => {
    const title = renameValue.trim();
    setRenamingId(null);
    if (!title) return;
    setPanolar(ps => ps.map(p => p.id === id ? { ...p, title } : p));
    axios.patch(`${API}/panolar/${id}?client_id=${clientId}`, { title }).catch(() => load());
  };

  if (openBoard && openBoard.kind === 'sales') {
    return <SalesBoard clientId={clientId} boardTitle={openBoard.title} onBack={() => setOpenBoard(null)} />;
  }
  if (openBoard) {
    return <PanoDetail boardId={openBoard.id} boardTitle={openBoard.title} clientId={clientId} onBack={() => setOpenBoard(null)} />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <ScreenHeader
        title={t('Panolar')}
        subtitle={t("Bağlı platformlardan canlı widget'larla kendi panonu oluştur.")}
        right={<Button variant="solid" onClick={addPano}>{t('Yeni Pano')}</Button>}
      />

      {loading ? (
        <PoyaisSpinner size={36} pad={24}/>
      ) : (
      <Grid min={280} gap={14}>
        {panolar.map(p => {
          const Cmp = Icon[p.icon as keyof typeof Icon] || Icon.panolarGrid;
          const renaming = renamingId === p.id;
          return (
            <Card key={p.id} pad={0} hover style={{ cursor: renaming ? 'default' : 'pointer', position: 'relative' }} onClick={() => !renaming && setOpenBoard(p)}>
              <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 2, zIndex: 1 }}>
                <button onClick={(e) => { e.stopPropagation(); startRename(p); }}
                  style={{ width: 22, height: 22, borderRadius: 7, border: 'none', background: 'transparent', color: 'var(--py-text-faint)', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  title={t('Yeniden adlandır')}><Icon.pencil size={12} sw={2} color="currentColor" /></button>
                <button onClick={(e) => { e.stopPropagation(); removePano(p.id); }}
                  style={{ width: 22, height: 22, borderRadius: 7, border: 'none', background: 'transparent', color: 'var(--py-text-faint)', cursor: 'pointer', fontSize: 13, lineHeight: 1 }}
                  title={t('Panoyu sil')}>✕</button>
              </div>
              <div style={{ padding: '20px 22px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <span style={{ width: 32, height: 32, borderRadius: 9, background: p.icon_bg, color: p.icon_color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Cmp size={17} sw={1.9} /></span>
                  <Badge color={p.tag_color}>{p.tag}</Badge>
                </div>
                {renaming ? (
                  <input autoFocus value={renameValue} onChange={e => setRenameValue(e.target.value)}
                    onClick={e => e.stopPropagation()}
                    onBlur={() => saveRename(p.id)}
                    onKeyDown={e => { if (e.key === 'Enter') saveRename(p.id); if (e.key === 'Escape') setRenamingId(null); }}
                    style={{ width: '100%', boxSizing: 'border-box' as const, fontSize: 15, fontWeight: 800, letterSpacing: '-0.015em', marginBottom: 5,
                      border: `1px solid ${T.accent}`, borderRadius: 7, padding: '3px 7px', fontFamily: 'inherit', outline: 'none' }} />
                ) : (
                  <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.015em', marginBottom: 5 }}>{p.title}</div>
                )}
                <div style={{ fontSize: 12.5, color: 'var(--py-text-mid-alt)', marginBottom: 14 }}>
                  {p.kind === 'sales' ? t('Tüm satış kanallarının cirosu, siparişi, ürünleri ve finansı.') : (p.widget_count ?? 0) > 0 ? `${p.widget_count} widget` : t('Henüz widget eklenmedi')}
                </div>
                <div style={{ display: 'flex', gap: 18, paddingTop: 13, borderTop: `1px solid ${T.border}` }}>
                  <div>
                    <div style={{ fontSize: 10.5, color: 'var(--py-text-mid-alt)', marginBottom: 3 }}>{t('Widget')}</div>
                    <div style={{ fontSize: 14, fontWeight: 800, fontFamily: MONO, color: T.text }}>{p.widget_count ?? 0}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10.5, color: 'var(--py-text-mid-alt)', marginBottom: 3 }}>{t('Son güncelleme')}</div>
                    <div style={{ fontSize: 14, fontWeight: 800, fontFamily: MONO, color: T.text }}>{relTime(p.last_widget_update, lang)}</div>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}

        <div onClick={addPano} style={{ border: '1px dashed var(--py-border-soft)', borderRadius: 16, background: 'transparent',
          padding: '20px 22px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 10, cursor: 'pointer', minHeight: 180 }}>
          <span style={{ width: 36, height: 36, borderRadius: 11, background: 'var(--py-track-bg)', color: 'var(--py-text-mid-alt)',
            display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon.plus size={18} sw={2.2} /></span>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--py-text-mid-alt)' }}>{t('Yeni pano oluştur')}</span>
        </div>
      </Grid>
      )}
    </div>
  );
}
